import { gql } from '@apollo/client/core';
import { deliveryLocation, pickupLocation, loadRequirements, equipmentRequirements, specialtyFields, pickupLocationNoConfirmation, deliveryLocationNoConfirmation, loadRequirementsNoBlindTwic } from './fragments';


export const GET_CURRENT_USER = gql`
    query getCurrentUser {
        getCurrentUser {
            OktaId
            EmailAddress
            FirstName
            FullName
            LastName
            CarrierId
            UserId
        }

        getCarrierInfo {
            CarrierName
            CarrierCode
            IsCompliant
            IsHazmatCertified
            HasAutoTracking
            CargoInsExpirationDate
            LiabilityInsExpirationDate
            CargoInsRenewalEffectiveDate
            LiabilityInsRenewalEffectiveDate
            LoadTrackingVendorId
            UseMP
            HasReeferIns
            UnitCount
            ConnectionMethodTypeId
            IsHighRiskCertified
        }

        getIsCommittedCarrier {
            Committed
        }
    }
`;

export const GET_CARRIER_REP = gql`
    query getCarrierRep {
        getCurrentUser {
            UserId
            CarrierRep {
                UserId
                PictureUrl
                EmailAddress
                FullName
                FirstName
                LastName
                TeamEmailAddress
                UserPhoneNumber
                DirectManagerUserFullName
                KeypointUserCode
            }
        }
    }
`;

/*
    WARNING
    BE AWARE - Not every field that is present on the LoadType is present on the Load Search endpoint.
    If you need to request a new field that isn't present in the `selectedLoadBoardFields` array in 'schema/load/loadHelpers'
    make a BE ticket to add it to the SearchService so we don't have bad performance on this request
*/
export const GET_LOAD_LIST = gql`
    query getLoads($loadFilter: LoadFilter!, $loadSort: SortBy!, $pagination: PaginationInput!) {
        getLoads(loadFilter: $loadFilter, loadSort: $loadSort, pagination: $pagination) {
            data {
                LoadBoardId
                LoadStatus
                CreatedOn
                ...deliveryLocation
                EquipmentType
                EquipmentTypeId
                Miles
                NumberOfPickups
                NumberOfDeliveries
                ...pickupLocation
                Weight
                NumberOfStops
                IsBookItNow
                TopSpend
                IsHighValue
                IsHighRisk
                CargoValue
                CustomerTrackingFrequencyTypeId
                CustomerRepEmailAddress
                PickupApptTypeId
                DeliveryApptTypeId
                ...specialtyFields
            }
            totalRecords
        }
    }
    ${deliveryLocation}
    ${pickupLocation}
    ${specialtyFields}
`;

export const GET_RECENT_LANE_COUNT = gql`
    query getRecentLaneCount($lanes: [LaneCountFilter!]!) {
        laneCounts(lanes: $lanes) {
            lane {
                filterTime
            }
            totalRecords
        }
    }
`;

export const GET_LOAD_DETAIL_DRAWER = gql`
    query loadDetailDrawer($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            LoadTypeId
            LoadStatus
            RowVersion
            CargoValue
            Commodities {
                LoadCommodityId
                Description
                Width
                Length
                Height
                HazmatClass
            }
            PieceCount
            PieceTypes {
                Description
                PieceTypeId
            }
            EquipmentSize
            SpaceNeeded
            LoadType
            IsHighValue
            IsHighRisk
            EquipmentTypeName
            PickupApptTypeId
            DeliveryApptTypeId
            CustomerCode
            ...equipmentRequirements
            ...loadRequirements

            ...pickupLocation
            ...deliveryLocation
            ...specialtyFields
            EquipmentType
            EquipmentTypeId
            Miles
            Weight
            NumberOfPickups
            NumberOfDeliveries
            NumberOfStops
            TopSpend
            CustomerTrackingFrequencyTypeId
            LoadEquipmentSpecialServices {
                Quantity
                LoadSpecialServiceId
            }
        }
    }

    ${pickupLocation}
    ${deliveryLocation}
    ${specialtyFields}
    ${equipmentRequirements}
    ${loadRequirements}
`;

export const GET_LOAD_DETAIL_DRAWER_V2 = gql`
    query loadDetailDrawerV2($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            LoadTypeId
            LoadStatus
            RowVersion
            CargoValue
            Commodities {
                LoadCommodityId
                Description
                Width
                Length
                Height
                HazmatClass
            }
            PieceCount
            PieceTypes {
                Description
                PieceTypeId
            }
            EquipmentSize
            SpaceNeeded
            LoadType
            IsHighValue
            IsHighRisk
            EquipmentTypeId
            EquipmentTypeName
            PickupApptTypeId
            DeliveryApptTypeId
            CustomerCode
            EquipmentType
            Miles
            Weight
            NumberOfPickups
            NumberOfDeliveries
            NumberOfStops
            CustomerTrackingFrequencyTypeId
            LoadEquipmentSpecialServices {
                Quantity
                LoadSpecialServiceId
            }
            ...loadRequirementsNoBlindTwic
            ...equipmentRequirements
            ...pickupLocationNoConfirmation
            ...deliveryLocationNoConfirmation
        }
        getLoadsByIdsCogSearch(loadNumbers: [$loadNumber]) {
            PickupApptStatusConfirmed
            DeliveryApptStatusConfirmed
            ...specialtyFields
            TopSpend
        }
    }
    ${specialtyFields}
    ${equipmentRequirements}
    ${loadRequirementsNoBlindTwic}
    ${pickupLocationNoConfirmation}
    ${deliveryLocationNoConfirmation}
`;

export const GET_LOAD_DETAIL_COMMITTED_CAPACITY = gql`
    query loadDetailCommittedCapacity($loadNumber: Int!) {
        getLoadByIdForCommittedCapacity(loadNumber: $loadNumber) {
            CargoValue
            EquipmentType
            LoadBoardId
            Miles
            PickupEarlyCity
            PickupEarlyStateCode
            PickupApptEarliest
            PickupLocationIANACode
            RowVersion
            TopSpend
        }
    }
`;

export const GET_LOAD_TENDER_DETAILS = gql`
    query getLoadTenderDetails($tenderId: Int) {
        getLoadTenderDetails(tenderId: $tenderId) {
            isCarrierAddFsc
            loadTenderId
            carrierId
            carrierRate
            loadNumber
            offerExpirationDate
            offerId
            offerAmount
            createdOn
            carrierCommissionRepEmail
            isLoadUpdated
            truckEquipmentTypeId
        }
    }
`;

export const GET_LOCATIONS_SUGGESTIONS = gql`
    query getLocationSuggestions($searchInput: String) {
        getLocationSuggestions(searchInput: $searchInput) {
            data {
                CityName
                CityState
                Geolocation
                Id
                Name
                Value
                Zone
                CountryId
            }
        }
    }
`;

export const GET_LOAD_BY_ID = gql`
    query getLoadById($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            RowVersion
            LoadStatus
            LoadStatusId
            LoadType
            IsBookItNow
            TopSpend
            LoadTrackingVendorId
            IsMobileTracking
            ...deliveryLocation
            DeliveryGeolocation
            DeliveryApptTypeId
            ...pickupLocation
            PickupApptStatusConfirmed
            PickupGeolocation
            PickupApptTypeId
            Miles
            CargoValue
            IsHighValue
            IsHighRisk
            EquipmentType
            EquipmentTypeId
            EquipmentTypeName
            Weight
            TankerEndorsement
            IsHazmat
            IsTeam
            NumberOfPickups
            NumberOfDeliveries
            DocumentsCount
            CustomerTrackingFrequencyTypeId
            CustomerRepEmailAddress
            CustomerCode
            IsMyEdiLoad
            ...loadRequirements
            ...equipmentRequirements
            ...specialtyFields
        }
    }
    ${deliveryLocation}
    ${pickupLocation}
    ${loadRequirements}
    ${equipmentRequirements}
    ${specialtyFields}
`;

export const GET_LOAD_BY_ID_V2 = gql`
    query getLoadById($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            RowVersion
            LoadStatus
            LoadStatusId
            LoadType
            IsBookItNow
            LoadTrackingVendorId
            IsMobileTracking
            ...deliveryLocationNoConfirmation
            DeliveryGeolocation
            DeliveryApptTypeId
            ...pickupLocationNoConfirmation
            PickupGeolocation
            PickupApptTypeId
            Miles
            CargoValue
            IsHighValue
            IsHighRisk
            EquipmentType
            EquipmentTypeId
            EquipmentTypeName
            Weight
            TankerEndorsement
            IsHazmat
            IsTeam
            NumberOfPickups
            NumberOfDeliveries
            DocumentsCount
            CustomerTrackingFrequencyTypeId
            CustomerRepEmailAddress
            CustomerCode
            IsMyEdiLoad
            ...loadRequirementsNoBlindTwic
            ...equipmentRequirements
        }
        getLoadsByIdsCogSearch(loadNumbers: [$loadNumber]) {
            PickupApptStatusConfirmed
            DeliveryApptStatusConfirmed
            ...specialtyFields
            TopSpend
        }
    }

    ${specialtyFields}
    ${equipmentRequirements}
    ${loadRequirementsNoBlindTwic}
    ${pickupLocationNoConfirmation}
    ${deliveryLocationNoConfirmation}
`;

export const GET_LOAD_TRACKING_TYPE = gql`
query getLoadById($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            LoadTrackingVendorId
            IsMobileTracking
        }
    }
`;

export const GET_LOAD_REQS_FOR_BIN = gql`
query getLoadReqsForBIN($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadStatus
            LoadBoardId
            TopSpend
            RowVersion
            Miles
            CustomerRepEmailAddress
            CustomerCode
            EquipmentType
            NumberOfPickups
            NumberOfDeliveries

            LoadEquipmentSpecialServices {
                LoadSpecialServiceId
                LoadNumber
                Quantity
            }
            LoadStopSpecialServices {
                LoadSpecialServiceId
                LoadNumber
                Quantity
            }
            HasHazmatCommodities

            ...deliveryLocation
            ...pickupLocation
            ...loadRequirements
            ...equipmentRequirements
            ...specialtyFields
        }
    }
    ${deliveryLocation}
    ${pickupLocation}
    ${loadRequirements}
    ${equipmentRequirements}
    ${specialtyFields}
`;

export const GET_LOAD_DETAILS_AND_EQ = gql`
    query getLoadDetailsAndEQ($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            LoadTypeId
            LoadType
            Miles
            CargoValue
            EquipmentTypeName
            EquipmentTypeId
            EquipmentSize
            SpaceNeeded
            ...loadRequirements
            ...equipmentRequirements
            Commodities {
                LoadCommodityId
                Description
                FreightClass
                HandlingInstructions
                HazmatClass
                HazardousPackagingGroup
                Height
                IsStackable
                Length
                NMFCNumber
                PieceCount
                PieceTypeDescription
                PlacardsNeeded
                SafetyInstructions
                UNNumber
                Weight
                Width
                Length
                Height
            }
            LoadReferenceNumbers {
                ReferenceNumber
                ReferenceNumberTypeId
            }
            LoadEquipmentSpecialServices {
                LoadSpecialServiceId
                LoadNumber
                Quantity
            }
            MinTemp
            MaxTemp
            PreCoolTemp
            CustomerTrackingFrequencyTypeId
            Comments
        }
    }
    ${loadRequirements}
    ${equipmentRequirements}
`;

export const GET_LOCATIONS_ZONE = gql`
    query getLocationsZone($searchInput: String) {
        getLocationsZone(searchInput: $searchInput) {
            data {
                CityName
                CityState
                Geolocation
                Id
                Name
                Value
                Zone
                CountryId
                LocationIANACode
            }
        }
    }
`;

export const GET_LOAD_STOPS_BY_ID = gql`
    query getLoadStopsById($loadNumber: Int!) {
        getLoadStopsById(loadNumber: $loadNumber) {
            LoadStopId
            StopTypeId
            SequenceNumber
            Geolocation
            Direction
        }
    }
`;

export const GET_LOAD_STOPS_FULL_BY_ID = gql`
    query getLoadStopsFullById($loadNumber: Int!) {
        getLoadStopsById(loadNumber: $loadNumber) {
            LoadStopId
            StopTypeId
            ApptHoursId
            ApptTypeId

            Direction
            SequenceNumber
            NumberOfDeliveries
            NumberOfPickUps

            Geolocation
            City
            State

            FacilityName
            FacilityCode
            PostalCode
            FacilityDetail {
                FacilityId
                Code
                Name
                Address1
                Address2
                City
                StateCode
                PostalCode
                Country

                DriverAssist
                DropTrailerAvailable
                TwicNeeded
                RequireLumper
                OvernightParkingAvailable
                ScaleOnSite
                EscortAvailable
            }

            DriverAssist
            DropTrailer
            TwicNeeded
            RequireLumper
            OvernightParkingAvailable
            ScaleOnSite
            EscortAvailable

            LocationIANACode
            AppointmentEarliest
            AppointmentLatest
            MustArriveByDate

            Blind
            BlindNotes

            SchedulingContact
            ContactPhone
            Contact {
                ContactId
                FirstName
                LastName
                Name
                EmailAddress
                MobilePhoneNumber
                PhoneNumber
                PhoneExtension
            }

            LoadCommodities {
                LoadCommodityId
                Description
                HandlingInstructions

                HazmatClass
                HazardousPackagingGroup
                PlacardsNeeded
                SafetyInstructions
                UNNumber

                PieceCount
                PieceTypeDescription

                PiecePackingTypeId
                PiecePackingTypeCount

                NMFCNumber
                FreightClass
                Length
                Width
                Height
                IsStackable
                Weight
            }
            LoadStopSpecialServices
            LoadStopReferenceNumbers {
                LoadStopReferenceNumberId
                ReferenceNumberTypeId
                ReferenceNumber
            }

            DriverInstructions
            Notes
        }
    }
`;

export const GET_ROUTE_INFO_BY_ID = gql`
    query getRouteInfoById($loadNumber: Int!) {
        getRouteInfoById(loadNumber: $loadNumber) {
            Stops {
                LoadStopId
                Geolocation
            }
            TrimbleRoute {
                type
                coordinates
            }
        }
    }
`;

export const GET_LOAD_ACTIVITY = gql`
    query getLoadActivity($loadNumber: Int!) {
        getLoadActivity(loadNumber: $loadNumber) {
            Activity
            Date
        }
    }
`;

export const GET_CONTACT_REP_INFO = gql`
    query getContactRepInfo($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            DeliveryLateCity
            DeliveryLateStateCode
            PickupEarlyCity
            PickupEarlyStateCode
        }
    }
`;

export const GET_FACILITY_DETAIL_BY_ID = gql`
    query getFacilityDetailById($facilityId: Int!) {
        getFacilityDetailById(facilityId: $facilityId) {
           FacilityId
           Code
           Name
           Directions
           DriverInstructions
           SchedulingInstructions
           IsShipper
           IsReceiver
           IsWarehouse
           FacilityType
           TwicNeeded
           DriverAssist
           Address1
           Address2
           City
           StateCode
           PostalCode
           Country
           PhoneNumber
           FacilityHours
           LocationIANACode
        }
    }
`;

export const GET_BOOKED_SIDERAIL_DATA = gql`
    query getBookedLoadBarInfo($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            CarrierCharges {
                LoadCarrierCostId
                ChargeTypeId
                CostPerUnit
                Units
                TotalCost
                Comment
                Quantity
            }
            DriverInfo {
                Driver
                DriverPhone
                DriverPhoneCountryId
                TrailerNumber
                TruckNumber
                SecondDriver
                SecondDriverPhoneNumber
                SecondDriverPhoneCountryId
            }
            EmptyCityState
            EmptyDateTime
            DocumentsAreApproved
        }
    }
`;

export const GET_BOOKED_LOAD_BAR_INFO = gql`
    query getBookedLoadBarInfo($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            TopSpend
            LoadStatus
            Miles
            ...deliveryLocation
            ...pickupLocation
            CarrierCharges {
                LoadCarrierCostId
                ChargeTypeId
                CostPerUnit
                Units
                TotalCost
                Comment
                Quantity
            }
            DriverInfo {
                Driver
                DriverPhone
                DriverPhoneCountryId
                TrailerNumber
                TruckNumber
                SecondDriver
                SecondDriverPhoneNumber
                SecondDriverPhoneCountryId
            }
            IsTeam
            EmptyCityState
            EmptyDateTime
            DocumentsAreApproved
        }
    }
    ${deliveryLocation}
    ${pickupLocation}
`;

/*
    WARNING
    BE AWARE - Not every field that is present on the LoadType is present on the Load Search endpoint.
    If you need to request a new field that isn't present in the `selectedLoadBoardFields` array in 'schema/load/loadHelpers'
    make a BE ticket to add it to the SearchService so we don't have bad performance on this request
*/
export const GET_MY_LOADS = gql`
    query getMyLoads($pagination: PaginationInput!, $tab: String!, $sort: SortBy!, $filters: MyLoadsFilter!) {
        getMyLoads(pagination: $pagination, tab: $tab, sort: $sort, filters: $filters) {
            data {
                LoadBoardId
                LoadStatus

                DeliveryLateCity
                DeliveryLateStateCode
                DeliveryLocationIANACode
                DeliveryApptEarliest
                DeliveryApptLatest
                DeliveryApptStatusConfirmed
                DeliveryApptTypeId

                PickupEarlyCity
                PickupEarlyStateCode
                PickupLocationIANACode
                PickupApptEarliest
                PickupApptLatest
                PickupApptStatusConfirmed
                PickupApptTypeId

                NumberOfPickups
                NumberOfDeliveries

                Miles
                ...specialtyFields

                DriverInfo {
                    Driver
                    DriverPhone
                    TrailerNumber
                    TruckNumber
                    SecondDriver
                    SecondDriverPhoneNumber
                    VINNumber
                }
            }
            totalRecords
        }
    }
    ${specialtyFields}
`;

export const GET_LOAD_DOCUMENTS = gql`
    query getLoadDocuments($loadNumber: Int!, $documentSourceTypes: [Int]) {
        loadDocuments(LoadNumber: $loadNumber, DocumentSourceTypes: $documentSourceTypes) {
            LoadDocumentId
            Type
            Name
            AzureStorageLink
            DocumentSourceTypeId
            IsCombined
            CreatedOn
            UploadedByUserId
            UploadedByUserCode
            DocumentTypeId
            UploadedUserName
            FileName
            FileType
            IsLoadApprovedByHubtran
        }
    }
`;

export const GET_MY_LOADS_COUNTS = gql`
    query getMyLoadsCounts($filters: MyLoadsFilter!) {
        getMyLoadsCounts(filters: $filters) {
            tab
            totalRecords
        }
    }
`;

export const GET_CURRENT_TENDER = gql`
    query getCurrentTender($loadNumber: Int!) {
        getCurrentTender(loadNumber: $loadNumber) {
            LoadNumber
            RespondBy
            EdiLoadTenderStatusTypeId
            EdiLoadTenderTypeId
        }
    }
`;

export const GET_CARRIER_INFO = gql`
    query getCarrierInfo {
        getCarrierInfo {
            CarrierName
            DOTNumber
            Addresses {
                AddressTypeId
                Address1
                Address2
                City
                State
                Zip
            }
            PayToAddress {
                Address1
                Address2
                City
                State
                Zip
            }
            CarrierEquipment

            CargoInsExpirationDate
            LiabilityInsExpirationDate
            LoadTrackingVendorId
            HasReeferIns
            UnitCount
        }
    }
`;

export const GET_TRACKING_EVENTS = gql`
    query getTrackingEvents($loadNumber: Int!, $pageNumber: Int!) {
        getLoadTrackingById(loadNumber: $loadNumber, pageNumber: $pageNumber) {
            IsLoadStatusValid
            LoadTrackingStatusTypeId
            LoadTrackingVendorName
            AutoTrackingErrorMessage
            IsInDetention
            TotalLoadTrackingRecords
            LoadTrackingEvents {
                LoadTrackingId
                LoadTrackingLocationId
                City
                State
                LocationIANACode
                CheckCallName
                CheckCallType
                Description
                Notes
                LoadTrackingVendorId
                VendorName
                LoadTrackingLocationType
                EventDate
            }
        }
    }
`;

export const GET_USER_OFFERS = gql`
    query getUserOffers($filters: OffersFilters!) {
        getUserOffers(filters: $filters) {
            data {
                id
                amount
                bizCounterAmount
                originCity
                originState
                originEarliest
                originLatest
                originCityIANATimeZoneName
                originGeocode
                destinationCity
                destinationState
                destinationEarliest
                destinationLatest
                destinationCityIANATimeZoneName
                destinationGeocode
                loadNumber
                loadStatus
                status
                changeReason
                expiration
                enteredByUserId
                updatedOn
                timerStart
                topSpend
            }
            totalRecords
        }

    }
`;

export const GET_USER_OFFERS_FED = gql`
query UserOffers($arguments: GetOffersInput!, $order: [OfferSortInput!]) {
    offers(arguments: $arguments, order: $order) {
        nodes {
          amount
          bizCounterAmount
          id
          status
          changeReason
          updates {
              timerEnd
              timerStart
          }
          load {
            id
            stops {
              facility {
                city
                geolocation
                stateCode
                timeZone {
                  ianaName
                }
              }
              latest
              earliest
            }
            topSpend
          }
          emptyCity {
            name
            state {
              code
            }
            geocode
          }
          createdOn
        }
        totalCount
      }
  }
`

export const GET_ALL_OFFERS_COUNT = gql`
    query getAllOffers($filters: OffersFilters!) {
        getAllOffers(filters: $filters) {
            totalRecords
        }

    }
`;

export const GET_ALL_OFFERS_COUNT_FED = gql`
query OfferCount($arguments: GetOffersInput!, $where: OfferFilterInput) {
    offers(arguments: $arguments, where: $where) {
        nodes {
            id
            status
        }
        totalCount
    }
  }
`;

export const GET_CURRENT_OFFER = gql`
    query getUserOffers($loadNumber: Int!, $offerStatuses: [Int]) {
        getUserOffers(filters: { PageSize: 1, PageNumber: 1, LoadNumbers: [$loadNumber], OfferStatuses: $offerStatuses }) {
            data {
                id
                amount
                bizCounterAmount
                status
                changeReason
                expiration
                updatedOn
                timerStart
                loadNumber
                originCity
                originState
                originEarliest
                originLatest
                originCityIANATimeZoneName
                originGeocode
                destinationCity
                destinationState
                destinationEarliest
                destinationLatest
                destinationCityIANATimeZoneName
                destinationGeocode
                updateCount
            }
        }
    }
`;

export const GET_CURRENT_OFFER_FED = gql`
query CurrentOffer($arguments: GetOffersInput!, $order: [OfferSortInput!]) {
    offers(arguments: $arguments, order: $order) {
      nodes {
        amount
        bizCounterAmount
        id
        status
        changeReason
        updates {
            timerEnd
            timerStart
        }
        load {
          id
          stops {
            facility {
              city
              geolocation
              stateCode
              timeZone {
                ianaName
              }
            }
            latest
            earliest
          }
        }
        emptyCity {
          name
          state {
            name
          }
          geocode
        }
        createdOn
        updatesCount
      }
    }
  }`

export const GET_CURRENT_OFFER_FOR_OFFER_HISTORY = gql`
    query getUserOffers($loadNumber: Int!) {
        getUserOffers(filters: { PageSize: 1, PageNumber: 1, LoadNumbers: [$loadNumber] }) {
            data {
                id
                amount
                bizCounterAmount
                status
            }
        }
    }
`;

export const GET_CURRENT_OFFER_FOR_OFFER_HISTORY_FED = gql`
query Offers($arguments: GetOffersInput!, $first: Int, $order: [OfferSortInput!]) {
    offers(arguments: $arguments, first: $first, order: $order) {
      nodes {
        id
        status
        amount
        bizCounterAmount
        createdOn
      }
    }
  }
`;

export const GET_CURRENT_OFFER_SUMMARY = gql`
    query getOfferSummary($loadNumber: Int!, $id: Int!) {
        getOfferSummary(LoadNumber: $loadNumber, id: $id) {
                id
                emptyCity
                emptyDateTime
                emptyCityIANACode
                emptyCityLocation {
                    CityName
                    CityState
                    CountryId
                    Geolocation
                    Id
                    Name
                    Value
                    Zone
            }
        }
    }
`;

export const GET_CURRENT_OFFER_SUMMARY_FED = gql`
query OfferInfo($arguments: GetOffersInput!, $where: OfferFilterInput) {
    offers(arguments: $arguments, where: $where) {
      nodes {
        id
        emptyDateTime
        emptyCity {
          timeZone {
            ianaName
          }
          name
          state {
            code
          }
          zone
          geocode
          country {
            id
          }
          id
        }
      }
    }
  }`

export const GET_OFFERS_HISTORY = gql`
    query getOffersHistory($offerId: Int!) {
        getOffersHistoryById(offerId: $offerId) {
            data {
                id
                enteredByUserId
                amount
                bizCounterAmount
                from
                on
                status
                changeReason
                enteredBy {
                  keypointUserCode
                  firstName
                  lastName
                }
            }
            totalRecords
        }
    }
`;

export const GET_OFFERS_HISTORY_FED = gql`
query OffersHistory($arguments: GetOfferUpdatesInput!, $order: [OfferUpdateSortInput!]) {
    offerUpdates(arguments: $arguments, order: $order) {
      nodes {
        amount
        id
        enteredByUser {
          id
          firstName
          lastName
          code
        }
        bizCounterAmount
        changeReason
        on
        status
        from
      }
    }
  }
`

export const GET_EXTRA_BOOKING_MODAL_DETAILS = gql`
    query getExtraBookingModalDetails($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId
            TopSpend
            IsTeam
            DriverInfo {
                Driver
                DriverPhone
                DriverPhoneCountryId
                TrailerNumber
                TruckNumber
                SecondDriver
                SecondDriverPhoneNumber
                SecondDriverPhoneCountryId
            }
            ...deliveryLocation
            ...pickupLocation
            EmptyCityState
            EmptyDateTime
        }
    }
    ${deliveryLocation}
    ${pickupLocation}
`

export const GET_LOAD_INFO_FOR_BIN_MAO_REQUIREMENTS = gql`
    query getLoadInfoForBinMaoRequirements($loadNumber: Int!) {
        getLoadById(loadNumber: $loadNumber) {
            LoadBoardId

            CustomerTrackingFrequencyTypeId
            CustomerCode
            IsBookItNow
            IsHazmat
            IsHighValue
            IsHighRisk

            PickupApptEarliest
            PickupLocationIANACode
            PickupApptLatest
            PickupApptStatusConfirmed
        }
    }
`;

export const GET_LOAD_INFO_FOR_BIN_MAO_REQUIREMENTS_V2 = gql`
    query getLoadInfoForBinMaoRequirementsV2($loadNumbers: [Int!]) {
        getLoadsByIdsCogSearch(loadNumbers: $loadNumbers) {
            LoadBoardId

            CustomerTrackingFrequencyTypeId
            CustomerCode
            IsBookItNow
            IsHazmat
            IsHighValue
            IsHighRisk

            PickupApptEarliest
            PickupLocationIANACode
            PickupApptLatest
            PickupApptStatusConfirmed
        }
    }
`;

export const GET_LANE_OPPORTUNITIES = gql`
    query getLaneOpportunities($pagination: PaginationInput){
        getLaneOpportunities(pagination: $pagination){
            id
            customerCode
            origin
            destination
            loadsPW
            equipmentType
            dropTrailer
            conditions
            origin
            createdOn
        }
    }
`;

export const GET_COMMITMENTS_FOR_COMMITMENT_LIST = gql`
    query getCommitmentsForCommitmentList($commitmentFilter: CommitmentFilter!, $sortBy: SortBy!, $pagination: PaginationInput) {
        getCommitmentsById(commitmentFilter: $commitmentFilter, sortBy: $sortBy, pagination: $pagination) {
          carrierId
          origin
          destination
          rate
          fsc {
            rate
            add
          }
          loadsPerDay
          loadsPerWeek
          isEnabled
          isAutoBook
          milesByLane
          totalLoads
          totalMiles
          revenueAddedFsc
          commitmentDaysRemaining
          laneModifiedOn
          deliveryDays
          pickupDays
          leadTime
          truckEquipmentTypeId
          carrierRepEmail
          rateConEmail
          isHazmat
          isTeam
          isTankerEndorsement
          dropAtShipper
          dropAtConsignee
          maximumValueAccepted
          loadTenderEmail
        }
    }
`

export const GET_COMMITMENT_METRICS = gql`
    query getTenderMetrics($laneCarrierId: Int!) {
        getTenderMetricsById(laneCarrierId: $laneCarrierId) {
            tendersOfferedCount
            loadAcceptancePercentage
        }
    }
`
export const GET_ALL_COUNTRIES = gql`
    query getAllCountries {
        getAllCountries {
            CountryId
            Name
            PhoneCode
            Alpha2Code
        }
    }
`;

export const GET_CARRIER_MATCHES = gql`
    query getCarrierMatches($quantity: Int, $excludeLoadsWithOffersOrCR: Boolean) {
        getCarrierMatches(quantity: $quantity, excludeLoadsWithOffersOrCR: $excludeLoadsWithOffersOrCR) {
            matches {
                CarrierTruckMatchId
                CustomerTrackingFrequencyTypeId
                LoadBoardId
                LoadTypeId
                EquipmentTypeId
                TopSpend
                PickupApptStatusConfirmed
                PickupApptEarliest
                PickupApptLatest
                DeliveryApptEarliest
                DeliveryApptLatest
                EquipmentType
                IsBookItNow
                IsHazmat
                IsHighValue
                Miles
                Weight
                PickupEarlyCity
                PickupEarlyStateCode
                PickupLocationIANACode
                DeliveryLateCity
                DeliveryLateStateCode
                DeliveryLocationIANACode
            }
            totalRecordCount
        }
    }
`

export const GET_CARRIER_MATCHES_FULL_DETAILS = gql`
    query getCarrierMatches($quantity: Int, $excludeLoadsWithOffersOrCR: Boolean) {
        getCarrierMatches(quantity: $quantity, excludeLoadsWithOffersOrCR: $excludeLoadsWithOffersOrCR) {
            matches {
                CarrierTruckMatchId
                LoadBoardId
                LoadStatus
                CreatedOn
                EquipmentType
                EquipmentTypeId
                Miles
                NumberOfPickups
                NumberOfDeliveries
                Weight
                NumberOfStops
                IsBookItNow
                TopSpend
                IsHighValue
                CustomerTrackingFrequencyTypeId
                CustomerRepEmailAddress
                PickupApptTypeId
                DeliveryApptTypeId
                IsHazmat
                IsTeam
                IsOverSizeLoad
                IsBlind
                TwicNeeded
                DropTrailer
                TankerEndorsement
                PickupEarlyCity
                PickupEarlyStateCode
                PickupApptEarliest
                PickupApptLatest
                PickupDeadheadInMiles
                PickupLocationIANACode
                PickupApptStatusConfirmed
                DeliveryLateCity
                DeliveryLateStateCode
                DeliveryApptEarliest
                DeliveryApptLatest
                DeliveryDeadheadInMiles
                DeliveryLocationIANACode
            }
            totalRecordCount
        }
    }
`

export const GET_LOAD_STATUS = gql`
    query getLoadStatus($loadNumber: Int!) {
        getLoadStatus(loadNumber: $loadNumber) {
            LoadNumber
            LoadStatusTypeId
            LoadStatusTypeDescription
        }
    }
`;

export const GET_SPECIAL_SERVICES_BY_FILTER = gql`
    query getFilteredSpecialService(
        $specialServicesInput: SpecialServicesInput
    ) {
        getFilteredSpecialService(
            specialServicesInput: $specialServicesInput,
        ) {
            Result {
                LoadSpecialServiceId
                Code
                Description
                SectionType
                Type
                IsForLTL
            }
            Errors
            Message
        }
    }
`;


export const GET_PACKING_TYPES = gql`
    query getPackingTypes {
        getPackingTypes{
            PackingTypeId
            Description
        }
    }
`;
